import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import PushToggle from "../components/PushToggle";

export default function Profile() {
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  const [code, setCode] = useState("");

  const join = async () => {
    if (!code.trim()) return;
    try {
      const { data } = await api.post("/org/join", { invite_code: code.trim() });
      toast.success(`Joined ${data.organisation.name}`);
      await refresh();
      setCode("");
    } catch (e) { toast.error(e.response?.data?.detail || "Invalid code"); }
  };

  return (
    <div data-testid="profile-page" className="max-w-2xl">
      <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight">Profile</h1>

      <div className="glass-card p-7 mt-6">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-neonpink to-neoncyan flex items-center justify-center font-display text-2xl font-black">
            {user.name[0].toUpperCase()}
          </div>
          <div>
            <div className="font-display text-2xl font-extrabold">{user.name}</div>
            <div className="text-sm text-white/60">{user.email}</div>
            <div className="text-xs text-neoncyan mt-1 capitalize font-bold">{user.role}</div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 mt-6">
          <div className="glass-card p-4 text-center"><div className="text-2xl font-display font-black text-neonyellow">{user.xp}</div><div className="text-xs text-white/55">XP</div></div>
          <div className="glass-card p-4 text-center"><div className="text-2xl font-display font-black text-neonpink">{user.level}</div><div className="text-xs text-white/55">Level</div></div>
          <div className="glass-card p-4 text-center"><div className="text-2xl font-display font-black text-neongreen">{user.streak}</div><div className="text-xs text-white/55">Streak</div></div>
        </div>
      </div>

      <div className="glass-card p-7 mt-5">
        <h2 className="font-display text-xl font-extrabold">Your roadmap</h2>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3 mt-4 text-sm">
          <dt className="text-white/55">Age range</dt><dd>{user.age_range || "—"}</dd>
          <dt className="text-white/55">Location</dt><dd>{user.location || "—"}</dd>
          <dt className="text-white/55">Situation</dt><dd>{user.study_work || "—"}</dd>
          <dt className="text-white/55">Living</dt><dd>{user.living_situation || "—"}</dd>
          <dt className="text-white/55">Goals</dt><dd>{(user.goals || []).join(", ") || "—"}</dd>
          <dt className="text-white/55">Focus</dt><dd>{(user.focus_areas || []).join(", ") || "—"}</dd>
        </dl>
      </div>

      <div className="glass-card p-7 mt-5">
        <h2 className="font-display text-xl font-extrabold">Join an organisation</h2>
        <p className="text-sm text-white/60 mt-1">Got a code from your uni, school or council? Enter it here.</p>
        <div className="flex gap-2 mt-4">
          <input value={code} onChange={e => setCode(e.target.value)} placeholder="e.g. USYD2026"
            data-testid="org-code-input"
            className="flex-1 bg-white/5 border border-white/10 rounded-2xl px-4 py-2.5 text-sm outline-none focus:border-neoncyan"/>
          <button onClick={join} data-testid="org-join-button" className="btn-primary-neon text-sm">Join</button>
        </div>
        {user.organisation_id && <div className="mt-3 text-xs text-neongreen">✓ Linked to organisation</div>}
      </div>

      <PushToggle/>

      <button onClick={async () => { await logout(); nav("/"); }} data-testid="profile-logout"
        className="mt-6 btn-secondary-neon text-sm flex items-center gap-2">
        <LogOut size={14}/> Sign out
      </button>
    </div>
  );
}
